import React from "react";
import toast from "react-hot-toast";
import { usePapaParse } from "react-papaparse";
import { useNavigate } from "react-router-dom";
import { addGoodsCSV, uploadGoodImages } from "api/GoodsAPI";
import { CSVtoJSON, errMessageMapper, JSONtoCSV } from "utils";
import { DragStartEvent, DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { IDefaultImage } from "../GoodsForm";
import { useDisclosure } from "@chakra-ui/hooks";
import { getCategories } from "api/CategoryAPI";
import { ICategory } from "../types/category.interface";
import { getBrands } from "api/BrandAPI";
import { IBrand } from "../types/brand.interface";

export type CSVGoodsFormProps = {
  sku_product: string;
  image_link?: string;
  name: string;
  description: string;
  price: string;
  max_purchase: string;
  weight: string;
  category_selector: string;
  brand_selector: string;
  category_id?: number;
  brand_id?: number;
};

const useCSVGoodsForm = () => {
  const navigate = useNavigate();
  const { readRemoteFile } = usePapaParse();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [files, setFiles] = React.useState<File[]>([]);
  const [parsedCSV, setParsedCSV] = React.useState<CSVGoodsFormProps[]>([]);
  const [importStatus, setImportStatus] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [currRow, setCurrRow] = React.useState<CSVGoodsFormProps>();
  const [productImages, setProductImages] = React.useState<File[]>([]);
  const [defaultImages, setDefaultImages] = React.useState<IDefaultImage[]>([]);
  const [uploadProgress, setUploadProgress] = React.useState<number>(0);
  const [rowCategories, setRowCategories] = React.useState<
    {
      sku_product: string;
      value: ICategory;
    }[]
  >([]);
  const [rowBrands, setRowBrands] = React.useState<
    {
      sku_product: string;
      value: IBrand;
    }[]
  >([]);
  const [activeItem, setActiveItem] = React.useState<{
    id: number;
    image_uuid: string;
    path: string;
  }>();
  const [rowImages, setRowImages] = React.useState<
    {
      sku_product: string;
      files: File[];
      images: string[];
    }[]
  >([]);

  const dropFile = (files: File[]) => setFiles([...files]);
  const removeFile = (files: File[]) => setFiles([...files]);
  const arrangeFiles = (files: File[]) => setFiles([...files]);
  const changeFile = (files: File[]) => setFiles([...files]);

  const dropProductImages = (images: File[]) => setProductImages([...images]);
  const removeProductImages = (images: File[]) => setProductImages([...images]);
  const arrangeProductImages = (images: File[]) =>
    setProductImages([...images]);
  const changeProductImages = (images: File[]) => setProductImages([...images]);

  const openModal = (data: CSVGoodsFormProps) => {
    setCurrRow(data);
    onOpen();
  };

  const handleAddImageRow = (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    if (!currRow) return;

    const images = productImages.map((item) => URL.createObjectURL(item));

    setRowImages((prev) => [
      ...prev,
      {
        sku_product: currRow.sku_product,
        files: productImages,
        images,
      },
    ]);
    updateImagesParsedCSV(images, currRow.sku_product);
    onClose();
    setCurrRow(undefined);
    setProductImages([]);
  };

  const handleDragImageStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveItem(defaultImages.find((item) => item.id === active.id));
  };

  const handleDragImageEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeItem = defaultImages.find((item) => item.id === active.id);
    const overItem = defaultImages.find((item) => item.id === over.id);

    if (!activeItem || !overItem) return;

    const activeIndex = defaultImages.findIndex(
      (item) => item.id === active.id
    );
    const overIndex = defaultImages.findIndex((item) => item.id === over.id);

    if (activeIndex !== overIndex) {
      const res = arrayMove(defaultImages, activeIndex, overIndex);
      setDefaultImages(res);
    }
    setActiveItem(undefined);
  };

  const handleDragImageCancel = () => {
    setActiveItem(undefined);
  };

  const onRemoveDefaultImage = (id: number) => {
    setDefaultImages((prev) => prev.filter((item) => item.id !== id));
  };

  const updateImagesParsedCSV = (images: string[], sku_product: string) => {
    const tempParsedCSV = parsedCSV.map((item) => {
      if (item.sku_product === sku_product) {
        return {
          ...item,
          image_link: images.join(";"),
        };
      }
      return item;
    });

    setParsedCSV(tempParsedCSV);
  };

  const reserveImages = async (images: File[]) => {
    const imageIds: string[] = [];
    try {
      await uploadGoodImages(images, setUploadProgress)
        .then((res) => {
          toast.dismiss();
          if (res?.status === 201) {
            imageIds.push(
              res?.data.data.map((image: { id: string }) => {
                return image.id;
              })
            );
          }
        })
        .catch((err) => {
          toast.error(
            err?.response?.data?.message || "Gagal mengupload foto barang!"
          );
          return err;
        });
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Error uploading images");
      throw error;
    }

    return imageIds.join(";");
  };

  const mapFinalParsedCSV = async () => {
    try {
      const parsedCSV = await CSVtoJSON(files[0]);
      const finalParsedCSV = await Promise.all(
        parsedCSV.map(async (item) => {
          if (!item.sku_product) {
            return null; // Skip if sku_product is undefined, null, or empty string
          }

          const images = rowImages.find(
            (row) => row.sku_product === item.sku_product
          );
          const category = rowCategories.find(
            (row) => row.sku_product === item.sku_product
          );
          const brand = rowBrands.find(
            (row) => row.sku_product === item.sku_product
          );

          return {
            ...item,
            image_ids: images ? await reserveImages(images.files) : undefined,
            category_id: category ? category.value.id : undefined,
            brand_id: brand ? brand.value.id : undefined,
          };
        })
      );

      return finalParsedCSV.filter((item) => item !== null); // Remove null entries
    } catch (error) {
      console.error("Error mapping final CSV:", error);
      toast.error("Error mapping final CSV");
      throw error;
    }
  };

  const onSubmit = async (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    setIsLoading(true);

    if (files.length === 0) {
      toast("File CSV harus diimport!", { icon: "📂" });
      return;
    }

    if (!importStatus) {
      const reader = new FileReader();
      reader.readAsDataURL(files[0]);
      reader.onload = () => {
        const path = reader.result;
        readRemoteFile(path.toString(), {
          header: true,
          download: true,
          delimiter: ",",
          skipEmptyLines: true,
          complete: (results) => {
            setParsedCSV(results.data as CSVGoodsFormProps[]);
            setImportStatus(true);
          },
        });

        setIsLoading(false);
      };
    } else {
      try {
        const finalParsedCSV = await mapFinalParsedCSV();
        const blobCSV = new Blob([JSONtoCSV(finalParsedCSV)], {
          type: "text/csv",
        });
        const finalCSV = new File([blobCSV], "modified-csv-goods.csv", {
          type: "text/csv",
        });

        await addGoodsCSV(finalCSV)
          .then((res) => {
            if (res.status === 201 || res.status === 200) {
              navigate(`/admin/goods`);
              toast.success(res?.data?.message || "Barang berhasil diimport!");
            } else
              toast.error(res?.data?.message || "Gagal mengimport barang!");
          })
          .catch((err) => {
            toast.error(errMessageMapper(err));
          });
      } catch (error: any) {
        toast.error(errMessageMapper(error));
      }
    }

    setIsLoading(false);
  };

  const handleDownloadTemplate = () => {
    const fileUrl = "/templates/csv-products-template.csv";

    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = "csv-products-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const loadCategoryOptions = async (
    search: string,
    loadedOptions: any,
    { page }: any
  ) => {
    const res = await getCategories(page, search);
    const resJSON = [
      ...loadedOptions,
      ...res.data.data.data.map((item: ICategory) => ({
        ...item,
        value: item.id,
        label: item.name,
      })),
    ];

    return {
      options: resJSON,
      hasMore: resJSON.length >= 10,
      additional: {
        page: page + 1,
      },
    };
  };

  const loadBrandOptions = async (
    search: string,
    loadedOptions: any,
    { page }: any
  ) => {
    const res = await getBrands(page, search);
    const resJSON = [
      ...loadedOptions,
      ...res.data.data.data.map((item: IBrand) => ({
        ...item,
        value: item.id,
        label: item.name,
      })),
    ];

    return {
      options: resJSON,
      hasMore: resJSON.length >= 10,
      additional: {
        page: page + 1,
      },
    };
  };

  const handleOnCategoryChange = (
    value: ICategory,
    cell: CSVGoodsFormProps
  ) => {
    if (!value) {
      setRowCategories((prev) =>
        prev.filter((item) => item.sku_product !== cell.sku_product)
      );
      return;
    }
    const tempCategories = rowCategories.filter(
      (item) => item.sku_product !== cell.sku_product
    );
    setRowCategories(() => [
      ...tempCategories,
      {
        sku_product: cell.sku_product,
        value,
      },
    ]);
  };

  const handleOnBrandChange = (value: IBrand, cell: CSVGoodsFormProps) => {
    if (!value) {
      setRowBrands((prev) =>
        prev.filter((item) => item.sku_product !== cell.sku_product)
      );
      return;
    }
    const tempBrands = rowBrands.filter(
      (item) => item.sku_product !== cell.sku_product
    );
    setRowBrands(() => [
      ...tempBrands,
      {
        sku_product: cell.sku_product,
        value,
      },
    ]);
  };

  return {
    files,
    parsedCSV,
    isLoading,
    importStatus,
    setImportStatus,
    setParsedCSV,
    dropFile,
    removeFile,
    arrangeFiles,
    changeFile,
    defaultImages,
    setDefaultImages,
    activeItem,
    setActiveItem,
    handleDragImageStart,
    handleDragImageEnd,
    handleDragImageCancel,
    onRemoveDefaultImage,
    openModal,
    rowImages,
    isOpen,
    onOpen,
    onClose,
    onSubmit,
    handleAddImageRow,
    productImages,
    dropProductImages,
    removeProductImages,
    arrangeProductImages,
    changeProductImages,
    handleDownloadTemplate,
    uploadProgress,
    loadCategoryOptions,
    loadBrandOptions,
    rowCategories,
    rowBrands,
    handleOnCategoryChange,
    handleOnBrandChange,
  };
};

export default useCSVGoodsForm;
